import { useCallback, useEffect, useState } from "react";
import { listAdmins, createAdmin, deleteAdmin, type AdminUser } from "../api/tcg";

function AdminUsers({ token, isOwner }: { token: string; isOwner: boolean }) {
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setAdmins(await listAdmins(token));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Konnte Admins nicht laden.");
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    async function handleCreate() {
        setBusy(true); setMessage(null); setError(null);
        try {
            await createAdmin(token, username.trim(), password);
            setMessage("Admin angelegt.");
            setUsername(""); setPassword("");
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
        } finally { setBusy(false); }
    }

    async function handleDelete(id: number, name: string) {
        const confirmed = window.confirm(`Admin „${name}" wirklich löschen?\n\nDas kann nicht rückgängig gemacht werden.`);
        if (!confirmed) return;
        setBusy(true); setMessage(null); setError(null);
        try {
            await deleteAdmin(token, id);
            setMessage("Admin gelöscht.");
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
        } finally { setBusy(false); }
    }

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">Admin-Verwaltung</h2>
            <p className="admin-note" style={{ marginBottom: "18px" }}>
                {isOwner
                    ? "Als erster Admin (Owner) kannst du weitere Admins anlegen und entfernen."
                    : "Übersicht aller Admins. Anlegen und Entfernen ist dem ersten Admin (Owner) vorbehalten."}
            </p>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            {isOwner && (
                <>
                    <div className="admin-control-row">
                        <label className="admin-label">Benutzername (min. 3 Zeichen)</label>
                        <input className="admin-input" type="text" value={username}
                            onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
                        <label className="admin-label">Passwort (min. 8 Zeichen)</label>
                        <input className="admin-input" type="password" value={password}
                            onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                    </div>
                    <div className="admin-actions" style={{ marginBottom: "26px" }}>
                        <button className="admin-btn-primary" onClick={handleCreate}
                            disabled={busy || username.trim().length < 3 || password.length < 8}>
                            Admin anlegen
                        </button>
                    </div>
                </>
            )}

            <table className="admin-table">
                <thead>
                    <tr><th>Benutzername</th><th>Rolle</th>{isOwner && <th></th>}</tr>
                </thead>
                <tbody>
                    {admins.map((a) => (
                        <tr key={a.id}>
                            <td>{a.username}</td>
                            <td>{a.is_owner ? "Owner (erster Admin)" : "Admin"}</td>
                            {isOwner && (
                                <td style={{ whiteSpace: "nowrap" }}>
                                    {!a.is_owner && (
                                        <button className="admin-btn-secondary"
                                            onClick={() => handleDelete(a.id, a.username)} disabled={busy}>
                                            Löschen
                                        </button>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}

export default AdminUsers;